import Papa from 'papaparse';
import { cleanCell } from './classify.js';
import { isClubwiseTestEmail } from './testRecords.js';

// BROWSER-SIDE PROJECTION of a ClubWise CSV export.
//
// ⚠️⚠️ THIS IS THE PII BOUNDARY. A ClubWise export carries names, addresses,
// phones, emails, dates of birth, postcodes and card numbers. This function reads
// the whole file in the browser and RETURNS ONLY THE WHITELISTED COLUMNS; nothing
// else is kept, posted, stored or logged. The server then re-validates what it
// receives and never sees the rest. The whitelist is positional by header NAME:
// a column that is not listed here cannot reach the result because no code path
// reads it.
//
// The caller decodes the bytes (latin-1 / windows-1252) and passes text:
//   new TextDecoder('windows-1252').decode(await file.arrayBuffer())
// ⚠️ Not UTF-8: a name with an accented letter decodes to the replacement
// character under UTF-8, and the file is latin-1.

export type ClubwiseFileKind = 'leads' | 'joins';

export interface ClubwiseLeadRow {
  memberNumber: string;
  status: string;
  source: string;
  contactType: string;
  /**
   * ⚠️ TRUE only for a ClubWise TEST record (its email ends @clubwise.com). Set in the
   * browser from the Email column, which is read for that and nothing else; the email
   * itself never appears here. Absent for every ordinary row, so the wire shape of an
   * ordinary upload is unchanged. The server drops these rows and counts them.
   */
  testRecord?: boolean;
}

export interface ClubwiseJoinRow extends ClubwiseLeadRow {
  membershipType: string;
  /** dd/mm/yyyy as exported. Parsed and validated by the server. */
  joiningDate: string;
}

/** Normalised header names we read. EVERYTHING ELSE IS IGNORED. */
const H = {
  member: 'member number',
  status: 'status',
  source: 'source',
  contactType: 'contact type',
  membershipType: 'membership type',
  joiningDate: 'joining date',
  // ⚠️ Read ONLY to spot ClubWise test records (testRecords.ts). Never kept.
  email: 'email',
} as const;

const normHeader = (h: unknown): string =>
  typeof h === 'string' ? h.replace(/\s+/g, ' ').trim().toLowerCase() : '';

export type ProjectResult =
  | { ok: true; kind: 'leads'; rows: ClubwiseLeadRow[]; skippedBlank: number }
  | { ok: true; kind: 'joins'; rows: ClubwiseJoinRow[]; skippedBlank: number }
  | { ok: false; error: string };

/**
 * Project a decoded CSV to the whitelisted columns, detecting which file it is
 * from its HEADERS (a Joiners export has Joining Date + Membership Type; a New
 * Prospects export does not).
 *
 * ⚠️ A row with no member number is dropped and counted (`skippedBlank`) — it is
 * a spacer or total line, and a lead we cannot key cannot be stored.
 */
export function projectClubwiseCsv(text: string): ProjectResult {
  const parsed = Papa.parse<string[]>(text, { header: false, skipEmptyLines: 'greedy' });
  const table = parsed.data;
  if (table.length === 0) return { ok: false, error: 'That file is empty.' };

  const header = (table[0] ?? []).map(normHeader);
  const idx = (name: string): number => header.indexOf(name);
  const iMember = idx(H.member);
  const iStatus = idx(H.status);
  const iSource = idx(H.source);
  const iContact = idx(H.contactType);
  if (iMember < 0 || iStatus < 0 || iSource < 0 || iContact < 0) {
    return {
      ok: false,
      error:
        'That does not look like a ClubWise New Prospects or Joiners export (expected Member number, Status, Source and Contact Type columns).',
    };
  }
  const iEmail = idx(H.email);
  const iJoinDate = idx(H.joiningDate);
  const iMembership = idx(H.membershipType);
  const isJoins = iJoinDate >= 0;
  if (isJoins && iMembership < 0) {
    return { ok: false, error: 'That Joiners file has a Joining Date column but no Membership Type column.' };
  }

  const cell = (r: string[], i: number): string => cleanCell(i >= 0 ? r[i] : '');
  // ⚠️ `{ testRecord: true }` or nothing: the address is read here and goes no further.
  const testFlag = (r: string[]): { testRecord?: true } =>
    iEmail >= 0 && isClubwiseTestEmail(r[iEmail]) ? { testRecord: true } : {};
  let skippedBlank = 0;

  if (!isJoins) {
    const rows: ClubwiseLeadRow[] = [];
    for (const r of table.slice(1)) {
      const memberNumber = cell(r, iMember);
      if (memberNumber === '') {
        skippedBlank += 1;
        continue;
      }
      rows.push({
        memberNumber,
        status: cell(r, iStatus),
        source: cell(r, iSource),
        contactType: cell(r, iContact),
        ...testFlag(r),
      });
    }
    return { ok: true, kind: 'leads', rows, skippedBlank };
  }

  const rows: ClubwiseJoinRow[] = [];
  for (const r of table.slice(1)) {
    const memberNumber = cell(r, iMember);
    if (memberNumber === '') {
      skippedBlank += 1;
      continue;
    }
    rows.push({
      memberNumber,
      status: cell(r, iStatus),
      source: cell(r, iSource),
      contactType: cell(r, iContact),
      membershipType: cell(r, iMembership),
      joiningDate: cell(r, iJoinDate),
      ...testFlag(r),
    });
  }
  return { ok: true, kind: 'joins', rows, skippedBlank };
}
